import Skeleton from '@mui/material/Skeleton';
import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Box, FormControlProps, FormHelperText, InputAdornment, MenuItem, SxProps } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { REQUEST_STATUS } from '@shared/constants/otherConstants';
import { ALL_MODULES_TREE_ITEM_ID } from '../constants/modulesConstants';
import { selectGetModulesStatus, selectModulesLookup } from '../store/selectors';
import ModulesTreeViewCommon from './ModulesTreeView';
import isEqual from 'lodash/isEqual';
import Cancel from '@mui/icons-material/Cancel';
import { Module, ModuleID, ModuleSelectorValue } from '../types';
import { OutlinedInputProps } from '@mui/material/OutlinedInput/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import { LS_MODULE_TREE_HINT_SHOWN } from '@shared/constants/storagesKeysConstants';
import OverflowChipList from '@shared/components/others/OverflowChipList/OverflowChipList';
import UtmsButton from '@shared/components/ui/UtmsButton';

const defaultModulesDoNotAllowToApply: ModuleID[] = [];

const SKELETONS_WIDTH = [100, 180, 220, 150, 200, 130, 250, 160, 190, 140, 210, 170];

interface ModulesTreeViewSelectProps {
    value: ModuleSelectorValue;
    onChange: (newValue: ModuleSelectorValue) => void;
    label?: string;
    width?: string | number;
    fullWidth?: boolean;
    required?: boolean;
    disabled?: boolean;
    multiSelect?: boolean;
    doNotAllowDelete?: boolean;
    size?: 'small' | 'medium';
    moduleToIgnoreId?: ModuleID;
    modulesDoNotAllowToApply?: ModuleID[];
    showFullModuleName?: boolean;
    selectPaperStyle?: SxProps;
    inputProps?: OutlinedInputProps;
    formControlProps?: FormControlProps;
    error?: boolean;
}

const ModulesTreeViewSelect: FC<ModulesTreeViewSelectProps> = ({
    value,
    onChange,
    label,
    width,
    fullWidth,
    required,
    disabled,
    multiSelect,
    doNotAllowDelete,
    size,
    moduleToIgnoreId,
    modulesDoNotAllowToApply = defaultModulesDoNotAllowToApply,
    showFullModuleName = true,
    selectPaperStyle,
    inputProps,
    formControlProps,
    error,
}) => {
    const { t } = useTranslation();
    const modulesLookup = useSelector(selectModulesLookup);
    const getModulesStatus = useSelector(selectGetModulesStatus);
    const areModulesLoading = getModulesStatus === REQUEST_STATUS.LOADING;
    const modulesAreNotLoaded = getModulesStatus !== REQUEST_STATUS.SUCCESS;
    const [searchString, setSearchString] = useState('');
    const [currentSelectedModules, setCurrentSelectedModules] = useState<ModuleID[]>([]);
    const [expandedModules, setExpandedModules] = useState<ModuleID[]>([ALL_MODULES_TREE_ITEM_ID]);
    const [moduleTreeHintWasShown, setModuleTreeHintWasShown] = useState(
        Boolean(localStorage.getItem(LS_MODULE_TREE_HINT_SHOWN)),
    );
    const [open, setOpen] = useState(false);
    const [shouldRenderTree, setShouldRenderTree] = useState(false);

    const handleSelectOpen = () => {
        setOpen(true);
        requestAnimationFrame(() => {
            setShouldRenderTree(true);
        });
    };

    const handleSelectClose = () => {
        setOpen(false);
        setSearchString('');
        setTimeout(() => {
            setShouldRenderTree(false);
            setCurrentSelectedModules([]);
        }, 200);
    };

    useEffect(() => {
        if (!open || getModulesStatus !== REQUEST_STATUS.SUCCESS) {
            return;
        }

        const newSelection =
            multiSelect && Array.isArray(value)
                ? value
                : !multiSelect && !Array.isArray(value)
                  ? value
                      ? [value]
                      : [ALL_MODULES_TREE_ITEM_ID]
                  : [];

        setCurrentSelectedModules(newSelection);
    }, [open, getModulesStatus, multiSelect, value]);

    const appliedModulesObjects = useMemo(() => {
        if ((multiSelect && Array.isArray(value) && !value.length) || (!multiSelect && !value) || modulesAreNotLoaded) {
            return [];
        }

        const modules = Array.isArray(value) ? value : value ? [value] : [];

        return modules.reduce((acc: Module[], moduleId) => {
            const module = modulesLookup[moduleId];
            if (module) {
                acc.push(module);
            }
            return acc;
        }, []);
    }, [value, multiSelect, modulesLookup, modulesAreNotLoaded]);

    const selectedValueIsApplied = useMemo(() => {
        return multiSelect
            ? isEqual(
                  value,
                  currentSelectedModules.map((m) => Number(m)),
              )
            : value === Number(currentSelectedModules[0]);
    }, [multiSelect, value, currentSelectedModules]);

    const handleApplyModule = () => {
        if (!currentSelectedModules.some((module) => modulesDoNotAllowToApply.includes(module))) {
            const newValue = multiSelect
                ? currentSelectedModules.map((m) => Number(m))
                : currentSelectedModules[0] !== ALL_MODULES_TREE_ITEM_ID
                  ? Number(currentSelectedModules[0])
                  : null;
            onChange(newValue);
            handleSelectClose();
        }
    };

    const handleRemoveModule = (moduleIdToRemove: ModuleID) => {
        if (!Array.isArray(value)) {
            return;
        }

        onChange(value.filter((moduleId) => moduleId !== moduleIdToRemove));
    };

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === 'Enter') {
            handleApplyModule();
        }
    };

    const handleClearModulesSelect = () => {
        setCurrentSelectedModules([]);
        handleApplyModule();
    };

    const handleMouseDownClearIcon = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };

    const handleCloseForeverAlert = () => {
        setModuleTreeHintWasShown(true);
        localStorage.setItem(LS_MODULE_TREE_HINT_SHOWN, 'true');
    };

    return (
        <FormControl
            size={size || 'small'}
            fullWidth={fullWidth}
            required={required}
            disabled={disabled}
            error={error}
            {...formControlProps}
        >
            <InputLabel id="module-select-label">{label || t('module')}</InputLabel>
            <Select
                displayEmpty
                open={open}
                onClose={handleSelectClose}
                data-testid="module_selector"
                onOpen={handleSelectOpen}
                labelId="module-select-label"
                value={' '}
                input={
                    <OutlinedInput
                        sx={{
                            width: fullWidth ? undefined : width || '400px',
                            '& .MuiSelect-select': {
                                paddingRight: '0 !important',
                            },
                            height: 42,
                            ...inputProps?.sx,
                        }}
                        label={label || t('module')}
                        endAdornment={
                            Boolean(appliedModulesObjects.length) &&
                            !doNotAllowDelete && (
                                <InputAdornment position="start">
                                    <IconButton
                                        size="small"
                                        onClick={handleClearModulesSelect}
                                        onMouseDown={handleMouseDownClearIcon}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }
                        {...inputProps}
                    />
                }
                renderValue={() => {
                    if (areModulesLoading) {
                        return <Skeleton width={150} />;
                    }

                    if (!appliedModulesObjects.length) {
                        return (
                            <Box
                                sx={{
                                    minWidth: '100px',
                                    color: 'var(--text-disabled)',
                                }}
                            >
                                {t('all_modules_placeholder')}
                            </Box>
                        );
                    }

                    return (
                        <OverflowChipList
                            array={appliedModulesObjects}
                            getLabel={(module) => module[showFullModuleName ? 'path' : 'name']}
                            getId={(module) => module.pk}
                            chipProps={
                                multiSelect
                                    ? {
                                          deleteIcon: (
                                              <Cancel
                                                  onMouseDown={(e) => {
                                                      e.stopPropagation();
                                                  }}
                                              />
                                          ),
                                          onDelete: handleRemoveModule,
                                      }
                                    : {}
                            }
                        />
                    );
                }}
                MenuProps={{
                    MenuListProps: {
                        sx: {
                            padding: 0,
                            height: '100%',
                        },
                    },
                    PaperProps: {
                        sx: {
                            scrollbarGutter: 'stable',
                            padding: '0 8px',
                            height: 400,
                            width: 400,
                            ...selectPaperStyle,
                        },
                    },
                }}
            >
                {/* hack to disable MUI Select warning */}
                <MenuItem sx={{ position: 'absolute', visibility: 'hidden' }} value={' '} />
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', paddingTop: 0 }}>
                    {shouldRenderTree ? (
                        <ModulesTreeViewCommon
                            RootProps={{
                                sx: {
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.25,
                                    height: 600,
                                    overflowY: 'auto',
                                },
                            }}
                            ToolbarStyle={{
                                mt: 1,
                            }}
                            multiSelect={multiSelect}
                            disableKeyboardExpansion
                            moduleToIgnoreId={moduleToIgnoreId}
                            selectedModules={currentSelectedModules}
                            setSelectedModules={setCurrentSelectedModules}
                            searchString={searchString}
                            setSearchString={setSearchString}
                            expandedModules={expandedModules}
                            setExpandedModules={setExpandedModules}
                            handleKeyDown={handleKeyDown}
                        />
                    ) : (
                        <Box sx={{ py: 1 }}>
                            <Skeleton variant="rounded" width="100%" height={36} sx={{ transform: 'unset' }} />
                            <Box sx={{ display: 'flex', mt: 2, flexDirection: 'column', gap: 1, px: 1 }}>
                                {SKELETONS_WIDTH.map((width, index) => (
                                    <Skeleton
                                        key={index}
                                        variant="rectangular"
                                        width={width}
                                        height={16}
                                        sx={{ transform: 'unset' }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                    <Box
                        sx={{
                            padding: '8px 0',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1,
                        }}
                    >
                        {!moduleTreeHintWasShown && (
                            <Alert
                                sx={{ position: 'sticky', bottom: 45 }}
                                severity="info"
                                onClose={handleCloseForeverAlert}
                            >
                                {t('module-tree-hint')}
                            </Alert>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <UtmsButton
                                disabled={
                                    selectedValueIsApplied ||
                                    currentSelectedModules.some((module) => modulesDoNotAllowToApply.includes(module))
                                }
                                onClick={handleApplyModule}
                            >
                                {t('apply')}
                            </UtmsButton>
                            <UtmsButton color="secondary" onClick={handleSelectClose}>
                                {t('cancel')}
                            </UtmsButton>
                        </Box>
                    </Box>
                </Box>
            </Select>
            {error && (
                <FormHelperText>
                    {t('select')} {t('module')}
                </FormHelperText>
            )}
        </FormControl>
    );
};

export default ModulesTreeViewSelect;
